import {AccountInformationData, AppStateAccountData} from "../account/accountTypes";
import {algod} from "../../core/integrations/algosdk/AlgodManager";
import {AVAILABLE_WALLETS} from "./walletConstants";
import webStorage, {STORED_KEYS} from "../../util/storage/webStorage";
import {getAssetInformationById, isPoolToken} from "../../asset/util/assetUtils";
import {AssetListItemData} from "../../asset/util/assetModels";
import {WalletType} from "./walletTypes";
import {WalletContextReducerAction} from "./context/WalletContext.types";

function saveSelectedAccountAddressToWebStorage(address: string) {
  webStorage.local.setItem(STORED_KEYS.SELECTED_ACCOUNT_ADDRESS, address);
}

function getSelectedAccountAddressFromWebStorage() {
  return webStorage.local.getItem(STORED_KEYS.SELECTED_ACCOUNT_ADDRESS) as
    | undefined
    | string;
}

function saveWalletAndAccountsToWebStorage(
  accounts: AppStateAccountData[],
  walletType: WalletType
) {
  webStorage.local.setItem(
    STORED_KEYS.WALLET_ACCOUNTS,
    accounts.map((account) => account.address)
  );
  webStorage.local.setItem(STORED_KEYS.WALLET_TYPE, walletType);
}

function getWalletAccountsFromWebStorage(): string[] {
  return webStorage.local.getItem(STORED_KEYS.WALLET_ACCOUNTS) as string[];
}

function getWalletTypeFromWebStorage(): WalletType | null {
  return (webStorage.local.getItem(STORED_KEYS.WALLET_TYPE) as WalletType) || null;
}

function resetWalletItemsOnWebStorage() {
  return new Promise<undefined>((resolve, reject) => {
    try {
      webStorage.removeFromWebStorage(STORED_KEYS.WALLET_ACCOUNTS);
      webStorage.removeFromWebStorage(STORED_KEYS.SELECTED_ACCOUNT_ADDRESS);
      webStorage.removeFromWebStorage(STORED_KEYS.WALLET_TYPE);
      webStorage.removeFromWebStorage(STORED_KEYS.WALLET_CONNECT);

      resolve(undefined);
    } catch (error) {
      reject(error);
    }
  });
}

// Stores the asset ids that were problematic with getAssetInformationById
const SKIPPED_ASSETS_CACHE: number[] = [];

async function fetchAccountAssetDetails(
  account: AccountInformationData
): Promise<AssetListItemData[]> {
  let assets: AssetListItemData[] = [];

  if (account.assets?.length) {
    const prospectiveAssets = await Promise.all(
      account.assets.map<Promise<AssetListItemData | null>>(async (asset) => {
        try {
          if (SKIPPED_ASSETS_CACHE.includes(Number(asset.assetId))) {
            return null;
          }

          const {asset: assetInfo, isDeleted} = await getAssetInformationById(
            Number(asset.assetId)
          );

          if (isDeleted) {
            SKIPPED_ASSETS_CACHE.push(Number(asset.assetId));
            return null;
          }

          return {
            ...assetInfo,
            balance: asset.amount,
            is_liquidity_token: isPoolToken(assetInfo)
          };
        } catch (error) {
          console.error(error);
          // If `getAssetInformationById` fails, we will just ignore those assets. For example, that call may fail for destroyed assets.
          return null;
        }
      })
    );

    assets = prospectiveAssets.filter(Boolean) as AssetListItemData[];
  }

  return assets;
}

/**
 * maps AccountInformationData[] to AppStateAccountData[]
 */
async function mapAccountsToAppStateAccounts(
  accounts: AccountInformationData[]
): Promise<{detail: AppStateAccountData; assets: AssetListItemData[]}[]> {
  const finalAccountsData: {
    detail: AppStateAccountData;
    assets: AssetListItemData[];
  }[] = [];

  for (const account of accounts) {
    const nfdomain = undefined;
    const accountAssets = await fetchAccountAssetDetails(account);

    finalAccountsData.push({
      detail: {
        ...account,
        nfdomain
      },
      assets: accountAssets
    });
  }

  return finalAccountsData;
}

function isPeraWallet(walletType: undefined | null | WalletType) {
  return walletType === "pera-wallet";
}

function isDeflyWallet(walletType: undefined | null | WalletType) {
  return walletType === "defly-wallet";
}

function isWalletConnect(walletType: undefined | null | WalletType) {
  return walletType === WalletType.WalletConnect;
}

function isMobileWallet(
  walletType: null | WalletType
): walletType is Extract<WalletType, "pera-wallet" | WalletType.WalletConnect> {
  return (
    isPeraWallet(walletType) || isWalletConnect(walletType) || isDeflyWallet(walletType)
  );
}

function isValidWalletType(wallet: WalletType | null) {
  return Boolean(
    AVAILABLE_WALLETS.find((availableWallet) => availableWallet.id === wallet)
  );
}

function truncateAccountAddress(stringToTruncate: string) {
  /* eslint-disable no-magic-numbers */
  return `${stringToTruncate.substring(0, 6)}...${stringToTruncate.substring(
    stringToTruncate.length - 4
  )}`;
  /* eslint-enable no-magic-numbers */
}

function fetchAccounts(accounts: string[]) {
  return Promise.all(
    accounts.map((account) => algod.client.accountInformation(account).do())
  );
}

async function handleDisconnectSuccessful(
  dispatch: React.Dispatch<WalletContextReducerAction>
) {
  dispatch({
    type: "DISCONNECT"
  });

  await resetWalletItemsOnWebStorage();
}

export {
  fetchAccountAssetDetails,
  saveSelectedAccountAddressToWebStorage,
  getSelectedAccountAddressFromWebStorage,
  saveWalletAndAccountsToWebStorage,
  getWalletAccountsFromWebStorage,
  getWalletTypeFromWebStorage,
  resetWalletItemsOnWebStorage,
  mapAccountsToAppStateAccounts,
  isPeraWallet,
  isDeflyWallet,
  isWalletConnect,
  isMobileWallet,
  isValidWalletType,
  truncateAccountAddress,
  fetchAccounts,
  handleDisconnectSuccessful
};
